import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { AppState, AppStore } from '~/stores/app';
import { ClaimType } from '~/utils/constants';
import {
  makeLoader,
  matchShippingProtectionPath,
  redirect,
} from '~/utils/routing';
import { claimAddressSchema } from '~/utils/schemas';
import { isAddressPhoneRequired } from '~/utils/theme';
import type { SetNonNullable } from '~/utils/types';

const handleAddressValidation = <Data>({
  address,
  requirePhone,
  onSuccess,
}: {
  address: unknown;
  requirePhone?: boolean;
  onSuccess: (address: z.infer<typeof claimAddressSchema>) => Data;
}) =>
  match(
    claimAddressSchema
      .required({ phone: requirePhone || undefined })
      .safeParse(address),
  )
    .with({ success: true }, ({ data }) => onSuccess(data))
    .with({ success: false }, () => redirect('edit'))
    .exhaustive();

const handleShippingProtectionRoute =
  (appStore: AppStore) =>
  ({ shippingProtectionClaim, order }: AppState) =>
    handleAddressValidation({
      address: shippingProtectionClaim.address ?? order?.shippingAddress,
      onSuccess: (address) => {
        appStore.dispatch(
          appStore.actions.shippingProtectionClaim.setAddress(address),
        );

        return {
          address,
          nextStep: '../review',
        };
      },
    });

const handleCrewClaimRoute =
  (appStore: AppStore) =>
  ({
    state: { crewClaim, order, settings },
  }: {
    state: SetNonNullable<AppState, 'settings' | 'crewClaim'>;
  }) =>
    handleAddressValidation({
      address: crewClaim.address ?? order?.shippingAddress,
      requirePhone: isAddressPhoneRequired(settings.theme, crewClaim.claimType),
      onSuccess: (address) => {
        appStore.dispatch(appStore.actions.crewClaim.setAddress(address));
        return {
          address,
          nextStep:
            crewClaim.claimType === ClaimType.warranty ?
              `../warranty-review`
            : `../return-methods`,
        };
      },
    });

export const { loader, useLoaderData } = makeLoader(({ request }, appStore) =>
  match({
    isShippingProtectionClaim: matchShippingProtectionPath(request),
    state: appStore.getState(),
  })
    .with(
      { isShippingProtectionClaim: P.nonNullable, state: P.select() },
      handleShippingProtectionRoute(appStore),
    )
    .with(
      {
        isShippingProtectionClaim: P.nullish,
        state: { settings: P.nonNullable, crewClaim: P.nonNullable },
      },
      handleCrewClaimRoute(appStore),
    )
    .otherwise(
      (data) =>
        new Error('Invalid state for address review loader', {
          cause: { data, request },
        }),
    ),
);
